import { useState, useCallback, useMemo } from 'react';

import { useIntl } from 'react-intl';

import Grid from '@mui/material/Grid2';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';

import {
  useGetFines,
  useUpdateFine,
  useDeleteFines,
  FinesStatusType,
  useUpdatePayFine,
  useUpdateExemptFines,
  useUpdateRevertFines,
  useCreateFinesReports,
  useUpdateApproveFines,
} from 'src/pages/FinesPage/api';
import { locale } from 'src/config';
import SearchFilterBar, {
  type QuickFiltersType,
} from 'src/components/SearchFilterBar';
import globalMessages from 'src/messages';
import { forceString } from 'src/utils/datetime';
import DataTable from 'src/components/DataTable';
import TextInput from 'src/components/TextInput';
import PageLayout from 'src/components/PageLayout';
import {
  checkCurrencyTyping,
  formatNumber,
  toParsableNumber,
} from 'src/utils/standards';
import CheckButton from 'src/components/CheckButton';
import ConfirmationDialog from 'src/components/ConfirmationDialog';
import JobLink from 'src/components/JobLink';

import messages from './messages';
import ExemptFineFormModal from './components/ExemptFineFormModal';
import CreateFineFormModal from './components/CreateFineFormModal';
import PreviousExportFinesFormModal from './components/PreviousExportFinesFormModal';

const checkParam = { locale };

const navTabs: Array<{ value: FinesStatusType; label: string }> = [
  {
    value: 'pending',
    label: 'Pending',
  },
  {
    value: 'approved',
    label: 'Approved',
  },
  {
    value: 'exported',
    label: 'Exported',
  },
  {
    value: 'paid',
    label: 'Paid',
  },
  {
    value: 'exempted',
    label: 'Exempted',
  },
];

interface ExplanationRef {
  [id: string]: {
    externalExplanation?: string | null;
    adminComment?: string | null;
    amount?: string | null;
  };
}

const fineFieldSx = { input: { textAlign: 'center' } };

export default function FinesPage() {
  const { formatMessage } = useIntl();

  const [activeTab, setActiveTab] = useState<FinesStatusType>(navTabs[0].value);
  const [usersFines, setUsersFines] = useState<ExplanationRef>({});
  const [selected, setSelected] = useState<UCM.FinesType[]>([]);
  const [fineErrors, setFineErrors] = useState<Record<string, string>>({});

  const [filters, setFilters] = useState<Record<string, string>>({});
  const [params, setParams] = useState<Record<string, string | number>>({
    page: 1,
  });

  const { data: fines, isFetching: isFetchingFines } = useGetFines({
    filters: {
      ...filters,
      status: activeTab,
    },
    params,
  });

  const { mutate: updateFine, isPending: isUpdatingFine } = useUpdateFine();
  const { mutate: updatePayFine, isPending: isUpdatingPayFine } =
    useUpdatePayFine();

  const { mutate: updateApproveFines, isPending: isUpdatingApproveFines } =
    useUpdateApproveFines();

  const { mutate: deleteFines, isPending: isDeletingFines } = useDeleteFines();

  const { mutate: updateExemptFines, isPending: isUpdatingExemptFines } =
    useUpdateExemptFines();

  const { mutate: updateRevertFines, isPending: isUpdatingRevertFines } =
    useUpdateRevertFines();

  const { mutate: createFinesReports, isPending: isCreatingFinesReports } =
    useCreateFinesReports();

  const validateFineAmount = useCallback(
    (value: string | null | undefined, fineId: string) => {
      if (!value || !value.trim()) {
        setFineErrors((prev) => ({
          ...prev,
          [`amount_${fineId}`]: formatMessage(globalMessages.requiredField),
        }));
        return false;
      }

      if (!checkCurrencyTyping(value, checkParam)) {
        setFineErrors((prev) => ({
          ...prev,
          [`amount_${fineId}`]: formatMessage(globalMessages.notTypeField, {
            type: 'number',
          }),
        }));
        return false;
      }

      const parsedValue = parseFloat(toParsableNumber(value, locale));
      if (Number.isNaN(parsedValue)) {
        setFineErrors((prev) => ({
          ...prev,
          [`amount_${fineId}`]: formatMessage(globalMessages.notTypeField, {
            type: 'number',
          }),
        }));
        return false;
      } else if (parsedValue <= 0) {
        setFineErrors((prev) => ({
          ...prev,
          [`amount_${fineId}`]: formatMessage(globalMessages.minValueField, {
            min: '0',
          }),
        }));
        return false;
      }

      setFineErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[`amount_${fineId}`];
        return newErrors;
      });

      return true;
    },
    [],
  );

  const handleSaveFine = useCallback(
    (uuid: string) => {
      const fine = usersFines[uuid];
      if (!fine) return;

      const isValid = validateFineAmount(fine.amount, uuid);
      if (!isValid) return;

      updateFine({
        ...fine,
        uuid,
      });
    },
    [usersFines],
  );

  const handleChangeFine = useCallback(
    (
      event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
      item: UCM.FinesType,
      key: string,
    ) => {
      const { value } = event.target;

      if (key === 'amount' && !checkCurrencyTyping(value, checkParam)) return;

      const localData = usersFines[item.uuid] ?? {};
      const existFine = fines?.data.find((fine) => fine.id === item.id);

      let dataToSave = {
        ...existFine,
        ...localData,
        [key]: value,
      };

      setUsersFines((prevState) => ({
        ...prevState,
        [item.uuid]: dataToSave,
      }));
    },
    [usersFines, fines?.data],
  );

  const handlePaidFine = useCallback((uuid: string) => {
    if (!uuid) return;

    updatePayFine({ uuid });
  }, []);

  const clearSelected = useCallback(() => {
    setSelected([]);
  }, []);

  const checkIfExist = useCallback(
    (uuid: string) => {
      return selected.findIndex((item) => item.uuid === uuid) !== -1;
    },
    [selected],
  );

  const isSelectAll = useMemo(() => {
    return fines?.data?.length !== 0 && selected.length === fines?.data?.length;
  }, [selected.length, fines?.data?.length]);

  const handleSelectAll = useCallback(() => {
    if (isSelectAll) {
      setSelected([]);
    } else if (fines?.data) {
      const allFines = fines?.data?.map((item) => item);
      setSelected(allFines);
    }
  }, [fines?.data, isSelectAll]);

  const handleSelect = useCallback(
    (fine: UCM.FinesType) => {
      const exist = checkIfExist(fine.uuid);
      let local = [...selected];
      if (exist) {
        const filtered = local.filter((item) => item.uuid !== fine.uuid);
        local = filtered;
      } else {
        local.push(fine);
      }
      setSelected(local);
    },
    [selected],
  );

  const fixedColumns = useMemo(() => {
    return [
      {
        id: 'selection',
        label: (
          <CheckButton active={isSelectAll} onChange={handleSelectAll}>
            <Typography variant="inherit">
              {formatMessage(messages.fineIdColumnLabel)}
            </Typography>
          </CheckButton>
        ),
        render: (_: unknown, item: UCM.FinesType) => {
          if (!item?.uuid) return '-';
          const isSelected = checkIfExist(item?.uuid);
          return (
            <CheckButton
              active={isSelected}
              onChange={() => handleSelect(item)}
            >
              <Typography variant="inherit">{item.id}</Typography>
            </CheckButton>
          );
        },
        width: 70,
      },
      {
        id: 'created',
        label: formatMessage(messages.createdByColumnLabel),
        render: (_: unknown, item: UCM.FinesType) => {
          return (
            <>
              <Typography variant="inherit">{item.createdBy}</Typography>
              <Typography variant="inherit">{item.createdAt}</Typography>
            </>
          );
        },
        align: 'center',
      },
      {
        id: 'affectedDate',
        label: formatMessage(messages.affectedDateColumnLabel),
        render: (affectedDate: string) => (
          <Typography variant="inherit">{forceString(affectedDate)}</Typography>
        ),
        align: 'center',
      },
      {
        id: 'studentName',
        label: formatMessage(messages.studentNameColumnLabel),
        render: (_: object, item: UCM.FinesType) => (
          <Typography variant="inherit">
            {`${item.user.id} - ${item.user.firstName} ${item.user.lastName}`}
          </Typography>
        ),
      },
      {
        id: 'cancellationNoticeId',
        label: formatMessage(messages.cancellationIdColumnLabel),
        render: (cancellationNoticeId: number) => (
          <Typography variant="inherit">{cancellationNoticeId}</Typography>
        ),
        align: 'center',
      },
      {
        id: 'jobId',
        label: formatMessage(messages.jobIdColumnLabel),
        render: (jobId: string, { job }: UCM.FinesType) => (
          <JobLink
            jobId={jobId}
            projectUuid={job.projectUuid}
            target="_blank"
          />
        ),
        align: 'center',
      },
      {
        id: 'exportId',
        label: formatMessage(messages.exportIdColumnLabel),
        align: 'center',
        render: (_: unknown, fine: UCM.FinesType) => {
          const { finesReportId } = fine;

          return (
            <>
              <Typography variant="inherit">{finesReportId ?? ''}</Typography>
              <Typography variant="inherit">
                {finesReportId
                  ? `(${forceString(fine.finesReport.createdAt)})`
                  : '-'}
              </Typography>
            </>
          );
        },
      },
      {
        id: 'reason',
        label: formatMessage(messages.reasonColumnLabel),
        render: (_: unknown, item: UCM.FinesType) => (
          <Typography variant="inherit">{item.reason.title}</Typography>
        ),
        align: 'center',
      },
      {
        id: 'timeDeviation',
        label: formatMessage(messages.timeDeviationColumnLabel),
        render: (timeDeviation: string) => (
          <Typography variant="inherit">{timeDeviation ?? '-'}</Typography>
        ),
        align: 'center',
      },
      {
        id: 'explanation',
        align: 'center',
        maxWidth: '15vw',
        label: formatMessage(messages.explanationColumnLabel),
        render: (explanation: string) => (
          <Typography variant="inherit">{explanation}</Typography>
        ),
      },
      {
        id: 'amount',
        label: formatMessage(messages.fineAmountColumnLabel),
        width: 65,
        align: 'center',
        render: (amount: string, item: UCM.FinesType) => {
          const value = usersFines[item.uuid]?.amount ?? (amount || '');
          const errorKey = `amount_${item.uuid}`;

          const disabled = activeTab === 'paid' || activeTab === 'exempted';

          return (
            <>
              <Typography color="textSecondary">
                {formatNumber(item.defaultAmount, locale) || '-'}
              </Typography>
              <TextInput
                fullWidth
                name="amount"
                value={value}
                sx={fineFieldSx}
                error={!!fineErrors[errorKey] && !disabled}
                helperText={fineErrors[errorKey]}
                disabled={disabled}
                onChange={(event) => handleChangeFine(event, item, 'amount')}
              />
            </>
          );
        },
      },
      {
        id: 'externalExplanation',
        label: formatMessage(messages.externalExplanationColumnLabel),
        render: (externalExplanation: string, item: UCM.FinesType) => {
          const value =
            usersFines[item.uuid]?.externalExplanation ??
            (externalExplanation || '');

          return (
            <TextInput
              name="externalExplanation"
              value={value}
              onChange={(event) =>
                handleChangeFine(event, item, 'externalExplanation')
              }
              size="small"
              disabled={activeTab === 'paid' || activeTab === 'exempted'}
              fullWidth
            />
          );
        },
      },
      {
        id: 'adminComment',
        maxWidth: '15vw',
        label: formatMessage(messages.adminCommentColumnLabel),
        render: (adminComment: string, item: UCM.FinesType) => {
          const value =
            usersFines[item.uuid]?.adminComment ?? (adminComment || '');

          return (
            <TextInput
              name="adminComment"
              value={value}
              onChange={(event) =>
                handleChangeFine(event, item, 'adminComment')
              }
              size="small"
              disabled={activeTab === 'paid' || activeTab === 'exempted'}
              fullWidth
            />
          );
        },
      },
    ];
  }, [
    usersFines,
    activeTab,
    handleChangeFine,
    isSelectAll,
    handleSelectAll,
    checkIfExist,
    handleSelect,
    fineErrors,
  ]);

  const pendingColumns = useMemo(() => {
    const excludedColumns = ['exportId'];
    return [
      ...fixedColumns.filter((column) => !excludedColumns.includes(column.id)),
      {
        id: 'actions',
        label: '',
        align: 'center',
        render: (_: unknown, item: UCM.FinesType) => (
          <IconButton
            onClick={() => handleSaveFine(item.uuid)}
            disabled={isUpdatingFine}
          >
            <SaveOutlinedIcon fontSize="small" />
          </IconButton>
        ),
      },
    ];
  }, [fixedColumns, handleSaveFine, isUpdatingFine]);

  const approvedColumns = useMemo(() => {
    const excludedColumns = ['exportId'];

    return [
      ...fixedColumns.filter((column) => !excludedColumns.includes(column.id)),
      {
        id: 'verified',
        label: formatMessage(messages.approvedByColumnLabel),
        render: (_: unknown, item: UCM.FinesType) => {
          return (
            <>
              <Typography variant="inherit">{item.verifiedBy}</Typography>
              <Typography variant="inherit">{item.verifiedAt}</Typography>
            </>
          );
        },
      },
      {
        id: 'actions',
        label: '',
        align: 'center',
        render: (_: unknown, item: UCM.FinesType) => (
          <IconButton
            color="inherit"
            size="small"
            onClick={() => handleSaveFine(item.uuid)}
            disabled={isUpdatingFine}
          >
            <SaveOutlinedIcon fontSize="small" />
          </IconButton>
        ),
      },
    ];
  }, [fixedColumns, handleSaveFine, isUpdatingFine]);

  const exportedColumns = useMemo(() => {
    return [
      ...fixedColumns,
      {
        id: 'verified',
        label: formatMessage(messages.approvedByColumnLabel),
        render: (_: unknown, item: UCM.FinesType) => {
          return (
            <>
              <Typography variant="inherit">{item.verifiedBy}</Typography>
              <Typography variant="inherit">{item.verifiedAt}</Typography>
            </>
          );
        },
      },
      {
        id: 'actions',
        label: '',
        align: 'center',
        render: (_: unknown, item: UCM.FinesType) => (
          <Stack flexDirection="row" alignItems="center" gap={1}>
            <IconButton
              color="inherit"
              size="small"
              onClick={() => handleSaveFine(item.uuid)}
              disabled={isUpdatingFine}
            >
              <SaveOutlinedIcon fontSize="small" />
            </IconButton>
            <ConfirmationDialog
              onConfirm={() => handlePaidFine(item.uuid)}
              message={formatMessage(messages.paidConfirmMessage)}
            >
              {(handleDialogOpen) => (
                <Button
                  color="secondary"
                  name="confirmButton"
                  onClick={handleDialogOpen}
                  disabled={isUpdatingPayFine}
                >
                  {formatMessage(messages.paidButtonLabel)}
                </Button>
              )}
            </ConfirmationDialog>
          </Stack>
        ),
      },
    ];
  }, [
    fixedColumns,
    handleSaveFine,
    handlePaidFine,
    isUpdatingFine,
    isUpdatingPayFine,
  ]);

  const paidColumns = useMemo(() => {
    return [
      ...fixedColumns,
      {
        id: 'verified',
        label: formatMessage(messages.approvedByColumnLabel),
        render: (_: unknown, item: UCM.FinesType) => {
          return (
            <>
              <Typography variant="inherit">{item.verifiedBy}</Typography>
              <Typography variant="inherit">{item.verifiedAt}</Typography>
            </>
          );
        },
      },
    ];
  }, [fixedColumns]);

  const columns: Record<
    FinesStatusType,
    Array<{
      id: string;
      label: string | React.ReactNode;
    }>
  > = useMemo(() => {
    const excludedColumns = ['selection'];

    return {
      pending: pendingColumns,
      approved: approvedColumns,
      exported: exportedColumns,
      exempted: paidColumns,
      paid: paidColumns.filter(
        (column) => !excludedColumns.includes(column.id),
      ),
    };
  }, [pendingColumns, approvedColumns, exportedColumns, paidColumns]);

  const searchFilters = useMemo(() => {
    return [
      {
        id: 'user_id',
        type: 'text' as QuickFiltersType['type'],
        placeholder: formatMessage(messages.searchUidPlaceholder),
      },
      {
        id: 'id',
        type: 'text' as QuickFiltersType['type'],
        placeholder: formatMessage(messages.searchFineIdPlaceholder),
      },
      {
        id: 'job_id',
        type: 'text' as QuickFiltersType['type'],
        placeholder: formatMessage(messages.searchJobIdPlaceholder),
      },
      {
        id: 'cancellation_notice_id',
        type: 'text' as QuickFiltersType['type'],
        placeholder: formatMessage(messages.searchCancellationIdPlaceholder),
      },
    ];
  }, []);

  const handleApproveFines = useCallback(() => {
    const uuids = selected.map((item) => item.uuid);
    updateApproveFines(
      { uuids },
      {
        onSuccess: clearSelected,
      },
    );
  }, [selected, clearSelected]);

  const handleDeleteFines = useCallback(() => {
    const uuids = selected.map((item) => item.uuid);
    deleteFines(
      { uuids },
      {
        onSuccess: clearSelected,
      },
    );
  }, [selected, clearSelected]);

  const handleExempt = useCallback(() => {
    const uuids = selected.map((item) => item.uuid);
    updateExemptFines(
      { uuids },
      {
        onSuccess: clearSelected,
      },
    );
  }, [selected, clearSelected]);

  const handleExportFines = useCallback(() => {
    const uuids = selected.map((item) => item.uuid);
    createFinesReports(
      { uuids },
      {
        onSuccess: clearSelected,
      },
    );
  }, [selected, clearSelected]);

  const handleRevertFines = useCallback(() => {
    const uuids = selected.map((item) => item.uuid);
    updateRevertFines(
      { uuids },
      {
        onSuccess: clearSelected,
      },
    );
  }, [selected, clearSelected]);

  const actionsButtons = useMemo(() => {
    const isMultiple = selected.length > 1;
    if (activeTab === 'pending') {
      return [
        {
          id: 'approve',
          label: formatMessage(messages.approveSelectedButtonLabel),
          onConfirm: handleApproveFines,
          confirmationMessage: isMultiple
            ? formatMessage(messages.approveFinesConfirmMessage)
            : formatMessage(messages.approveFineConfirmMessage),
        },
        {
          id: 'delete',
          label: formatMessage(messages.deleteSelectedButtonLabel),
          onConfirm: handleDeleteFines,
          confirmationMessage: isMultiple
            ? formatMessage(messages.deleteFinesConfirmMessage)
            : formatMessage(messages.deleteFineConfirmMessage),
        },
      ];
    } else if (activeTab.match(/approved|exempted/)) {
      return [
        activeTab === 'approved'
          ? {
              id: 'export',
              label: formatMessage(messages.exportSelectedButtonLabel),
              onConfirm: handleExportFines,
              confirmationMessage: isMultiple
                ? formatMessage(messages.exportFinesConfirmMessage)
                : formatMessage(messages.exportFineConfirmMessage),
            }
          : undefined,
        {
          id: 'revert',
          label: formatMessage(messages.revertSelectedButtonLabel),
          onConfirm: handleRevertFines,
          confirmationMessage: isMultiple
            ? formatMessage(messages.revertFinesConfirmMessage)
            : formatMessage(messages.revertFineConfirmMessage),
        },
      ];
    }

    return [];
  }, [selected, handleApproveFines, handleExportFines, handleRevertFines]);

  const handleActiveTabChange = useCallback((tab: FinesStatusType) => {
    setActiveTab(tab);
    setParams({ page: 1 });
    setSelected([]);
    setUsersFines({});
    setFineErrors({});
  }, []);

  const handlePagination = useCallback((_: unknown, page: number) => {
    setParams({ page: page + 1 });
  }, []);

  // eslint-disable-next-line
  const handleSearch = useCallback((filters: Record<string, any>) => {
    setParams({ page: 1 });
    setFilters(filters);
  }, []);

  const disableActions = useMemo(() => {
    return (
      selected.length === 0 ||
      isUpdatingFine ||
      isUpdatingPayFine ||
      isUpdatingApproveFines ||
      isDeletingFines ||
      isUpdatingRevertFines ||
      isUpdatingExemptFines ||
      isCreatingFinesReports
    );
  }, [
    selected,
    isUpdatingFine,
    isUpdatingPayFine,
    isUpdatingApproveFines,
    isDeletingFines,
    isUpdatingRevertFines,
    isUpdatingExemptFines,
    isCreatingFinesReports,
  ]);

  return (
    <PageLayout
      iconName="fines"
      title={formatMessage(messages.title)}
      tabs={navTabs}
      activeTab={activeTab}
      onActiveTabChange={handleActiveTabChange}
    >
      <Grid container spacing={2} alignItems="center">
        <Grid size={6}>
          <SearchFilterBar
            fields={searchFilters}
            onSearch={handleSearch}
            variant="noBorder"
          />
        </Grid>
        <Grid size={6} container alignItems="center">
          <Grid size={9} container spacing={2}>
            {activeTab === 'exported' && <PreviousExportFinesFormModal />}
            {actionsButtons?.map(
              (button) =>
                button && (
                  <ConfirmationDialog
                    onConfirm={button.onConfirm}
                    message={button.confirmationMessage}
                    key={button.id}
                  >
                    {(handleDialogOpen) => (
                      <Button
                        color="secondary"
                        name="confirmButton"
                        onClick={handleDialogOpen}
                        disabled={disableActions}
                      >
                        {button.label}
                      </Button>
                    )}
                  </ConfirmationDialog>
                ),
            )}
            {activeTab.match(/pending|approved/) && (
              <ExemptFineFormModal
                selected={selected}
                onConfirm={handleExempt}
                disabled={disableActions}
              />
            )}
          </Grid>
          <Grid
            size={3}
            container
            alignItems="center"
            justifyContent="flex-end"
          >
            <CreateFineFormModal />
          </Grid>
        </Grid>
        <Grid size={12}>
          <DataTable
            rows={fines?.data}
            pagination={fines?.meta}
            columns={columns[activeTab]}
            onPaginate={handlePagination}
            loading={isFetchingFines}
          />
        </Grid>
      </Grid>
    </PageLayout>
  );
}
